/**
 * Admin Speed Dating Overrides Page
 * ==================================
 * Allows admins to create, view, edit, and cancel extra speed dating events
 * beyond the default daily schedule.
 */

import { useEffect, useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../App";
import {
  ArrowLeft,
  Plus,
  Calendar,
  Video,
  MessageSquare,
  DollarSign,
  Shield,
  Clock,
  Users,
  X,
  Edit,
  Eye,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import {
  listExtraEvents,
  createExtraEvent,
  updateExtraEvent,
  cancelExtraEvent,
} from "../../api/adminExtraEvents";
import { track } from "../../utils/analytics";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { normalizeList } from "../../utils/normalize";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";

const MAX_EVENTS_PER_DAY = 10;

const STATUS_COLORS = {
  draft: "bg-slate-500",
  scheduled: "bg-blue-500",
  live: "bg-green-500",
  ended: "bg-gray-500",
  cancelled: "bg-red-500",
};

const STATUS_LABELS = {
  draft: "Draft",
  scheduled: "Scheduled",
  live: "Live",
  ended: "Ended",
  cancelled: "Cancelled",
};

function formatDateTime(isoString) {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatPrice(cents) {
  if (!cents) return "Free";
  return `$${(cents / 100).toFixed(2)}`;
}

export default function AdminSpeedDatingOverrides() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [todayCount, setTodayCount] = useState(0);
  const [activeTab, setActiveTab] = useState("today");

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" | "edit" | "view"
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState(getDefaultFormData());
  const [formWarnings, setFormWarnings] = useState([]);
  const [saving, setSaving] = useState(false);

  // Cancel confirm dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [eventToCancel, setEventToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  function getDefaultFormData() {
    const now = new Date();
    now.setHours(now.getHours() + 1, 0, 0, 0); // Round to next hour
    return {
      name: "",
      type: "video",
      starts_at: now.toISOString().slice(0, 16),
      duration_minutes: 60,
      capacity: 40,
      ticket_required: false,
      ticket_price_cents: 999,        // Default: $9.99
      vip_ticket_price_cents: 1499,   // Default: $14.99
      stripe_price_id: "",
      requires_verification: true,
      notes: "",
    };
  }

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listExtraEvents({ range: activeTab }, token);
      setEvents(normalizeList(data, ['events']));
      setTodayCount(data.extra_events_today || 0);
    } catch (err) {
      toast.error("Failed to load events: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [activeTab, token]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const openCreateModal = () => {
    setModalMode("create");
    setSelectedEvent(null);
    setFormData(getDefaultFormData());
    setFormWarnings([]);
    setModalOpen(true);
  };

  const openEditModal = (event) => {
    setModalMode("edit");
    setSelectedEvent(event);
    setFormData({
      name: event.name || "",
      type: event.type,
      starts_at: event.starts_at
        ? new Date(event.starts_at).toISOString().slice(0, 16)
        : "",
      duration_minutes: event.duration_minutes || 60,
      capacity: event.capacity || 40,
      ticket_required: event.ticket_required || false,
      ticket_price_cents: event.ticket_price_cents || 0,
      vip_ticket_price_cents: event.vip_ticket_price_cents || 0,
      stripe_price_id: event.stripe_price_id || "",
      requires_verification: event.requires_verification ?? true,
      notes: event.notes || "",
    });
    setFormWarnings([]);
    setModalOpen(true);
  };

  const openViewModal = (event) => {
    setModalMode("view");
    setSelectedEvent(event);
    setFormData({
      name: event.name || "",
      type: event.type,
      starts_at: event.starts_at
        ? new Date(event.starts_at).toISOString().slice(0, 16)
        : "",
      duration_minutes: event.duration_minutes || 60,
      capacity: event.capacity || 40,
      ticket_required: event.ticket_required || false,
      ticket_price_cents: event.ticket_price_cents || 0,
      vip_ticket_price_cents: event.vip_ticket_price_cents || 0,
      stripe_price_id: event.stripe_price_id || "",
      requires_verification: event.requires_verification ?? true,
      notes: event.notes || "",
    });
    setFormWarnings([]);
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setFormWarnings([]);

      const payload = {
        ...formData,
        starts_at: new Date(formData.starts_at).toISOString(),
        ticket_price_cents: formData.ticket_required
          ? formData.ticket_price_cents
          : null,
        vip_ticket_price_cents:
          formData.ticket_required && formData.vip_ticket_price_cents > 0
            ? formData.vip_ticket_price_cents
            : null,
        stripe_price_id: formData.stripe_price_id || null,
      };

      let result;
      if (modalMode === "create") {
        result = await createExtraEvent(payload, token);
        toast.success("Event created successfully!");
      } else {
        result = await updateExtraEvent(selectedEvent.id, payload, token);
        toast.success("Event updated successfully!");
      }

      const warnings = Array.isArray(result?.warnings) ? result.warnings : [];

      // ✅ CLOSE FIRST (deterministic)
      setModalOpen(false);

      // ✅ HARD RESET modal state so nothing can "re-open" it
      setSelectedEvent(null);
      setModalMode("create");
      setFormData(getDefaultFormData());
      setFormWarnings([]);

      // ✅ Refresh list
      fetchEvents();

      // ✅ Surface warnings outside the modal
      if (warnings.length) {
        toast.info(
          warnings.includes("overlap_nearby_event")
            ? "Heads up: another event is scheduled near this time."
            : `Heads up: ${warnings.join(", ")}`
        );
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!eventToCancel) return;

    try {
      setCancelling(true);
      await cancelExtraEvent(eventToCancel.id, token);
      toast.success("Event cancelled successfully");
      setCancelDialogOpen(false);
      setEventToCancel(null);
      fetchEvents();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const canEdit = (event) => {
    const status = event.derived_status || "scheduled";
    return ["draft", "scheduled"].includes(status) && !event.cancelled;
  };

  const canCancel = (event) => {
    const status = event.derived_status || "scheduled";
    return status === "scheduled" && !event.cancelled;
  };

  const isCapReached = todayCount >= MAX_EVENTS_PER_DAY;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100">
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <header className="border-b border-slate-800 pb-4">
          <button
            onClick={() => navigate("/admin")}
            className="inline-flex items-center space-x-2 text-sm text-slate-400 hover:text-slate-200 transition mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Admin</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Calendar className="w-6 h-6 text-purple-400" />
                Speed Dating Overrides
              </h1>
              <p className="text-sm text-slate-400 mt-1">
                Daily defaults stay on. These are additional events.
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* Counter pill */}
              <div
                className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                  isCapReached
                    ? "bg-red-500/20 text-red-300 border border-red-500/30"
                    : "bg-purple-500/20 text-purple-300 border border-purple-500/30"
                }`}
              >
                Extra events today: {todayCount} / {MAX_EVENTS_PER_DAY}
              </div>

              {/* Create button */}
              <Button
                onClick={openCreateModal}
                disabled={isCapReached}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Extra Event
              </Button>
            </div>
          </div>

          {isCapReached && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-300 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Daily cap reached (10/10). Try another day.
            </div>
          )}
        </header>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="today">Today</TabsTrigger>
            <TabsTrigger value="week">Upcoming (7 days)</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
              </div>
            ) : events.length === 0 ? (
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-300">
                    No extra events
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Create an extra event to get started.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {events.map((event) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onView={() => openViewModal(event)}
                    onEdit={canEdit(event) ? () => openEditModal(event) : null}
                    onCancel={
                      canCancel(event)
                        ? () => {
                            setEventToCancel(event);
                            setCancelDialogOpen(true);
                          }
                        : null
                    }
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit/View Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {modalMode === "create"
                ? "Create Extra Event"
                : modalMode === "edit"
                ? "Edit Extra Event"
                : "View Event"}
            </DialogTitle>
            <DialogDescription>
              {modalMode === "view"
                ? "Event details (read-only)"
                : "Fill in the details for this extra event."}
            </DialogDescription>
          </DialogHeader>

          {/* Warnings */}
          {formWarnings.length > 0 && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-300 text-sm">
              <div className="flex items-center gap-2 font-medium mb-1">
                <AlertTriangle className="w-4 h-4" />
                Warning
              </div>
              {formWarnings.includes("overlap_nearby_event") && (
                <p>Another {formData.type} event is scheduled near this time.</p>
              )}
            </div>
          )}

          <div className="space-y-4 py-2">
            {/* Name */}
            <div>
              <Label htmlFor="name">Event Name (optional)</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Extra Video Event"
                disabled={modalMode === "view"}
                className="bg-slate-800 border-slate-700"
              />
              <p className="text-xs text-slate-500 mt-1">
                Auto-generated if empty
              </p>
            </div>

            {/* Type */}
            <div>
              <Label>Event Type</Label>
              <Select
                value={formData.type}
                onValueChange={(val) =>
                  setFormData({ ...formData, type: val })
                }
                disabled={
                  modalMode === "view" ||
                  (modalMode === "edit" &&
                    ["live", "ended"].includes(
                      selectedEvent?.derived_status
                    ))
                }
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="video">
                    <div className="flex items-center gap-2">
                      <Video className="w-4 h-4" /> Video
                    </div>
                  </SelectItem>
                  <SelectItem value="text">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" /> Text
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date & Time */}
            <div>
              <Label htmlFor="starts_at">Start Date & Time</Label>
              <Input
                id="starts_at"
                type="datetime-local"
                value={formData.starts_at}
                onChange={(e) =>
                  setFormData({ ...formData, starts_at: e.target.value })
                }
                disabled={modalMode === "view"}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            {/* Duration */}
            <div>
              <Label>Duration</Label>
              <Select
                value={String(formData.duration_minutes)}
                onValueChange={(val) =>
                  setFormData({ ...formData, duration_minutes: parseInt(val) })
                }
                disabled={modalMode === "view"}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">60 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Capacity */}
            <div>
              <Label htmlFor="capacity">Capacity</Label>
              <Input
                id="capacity"
                type="number"
                min={2}
                max={200}
                value={formData.capacity}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    capacity: parseInt(e.target.value) || 40,
                  })
                }
                disabled={modalMode === "view"}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            {/* Ticketed toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-emerald-400" />
                <span>Ticketed Event</span>
              </div>
              <Switch
                checked={formData.ticket_required}
                onCheckedChange={(checked) =>
                  setFormData({
                    ...formData,
                    ticket_required: checked,
                    requires_verification: checked ? true : formData.requires_verification,
                  })
                }
                disabled={modalMode === "view"}
              />
            </div>

            {/* Ticket price (if ticketed) */}
            {formData.ticket_required && (
              <>
                <div>
                  <Label htmlFor="ticket_price">Standard Ticket Price (cents)</Label>
                  <Input
                    id="ticket_price"
                    type="number"
                    min={0}
                    value={formData.ticket_price_cents}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        ticket_price_cents: parseInt(e.target.value) || 0,
                      })
                    }
                    disabled={modalMode === "view"}
                    className="bg-slate-800 border-slate-700"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Standard: 999 = $9.99
                  </p>
                </div>

                <div>
                  <Label htmlFor="vip_price">VIP Ticket Price (cents, optional)</Label>
                  <Input
                    id="vip_price"
                    type="number"
                    min={0}
                    value={formData.vip_ticket_price_cents}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        vip_ticket_price_cents: parseInt(e.target.value) || 0,
                      })
                    }
                    disabled={modalMode === "view"}
                    className="bg-slate-800 border-slate-700"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    VIP: 1499 = $14.99 (0 to disable)
                  </p>
                </div>

                <div>
                  <Label htmlFor="stripe_price_id">Stripe Price ID (optional)</Label>
                  <Input
                    id="stripe_price_id"
                    value={formData.stripe_price_id}
                    onChange={(e) =>
                      setFormData({ ...formData, stripe_price_id: e.target.value })
                    }
                    placeholder="price_xxxxx"
                    disabled={modalMode === "view"}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </>
            )}

            {/* Verification toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-blue-400" />
                <span>Requires Verification</span>
              </div>
              <Switch
                checked={formData.requires_verification}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, requires_verification: checked })
                }
                disabled={modalMode === "view" || formData.ticket_required}
              />
            </div>
            {formData.ticket_required && (
              <p className="text-xs text-slate-500 -mt-2">
                Verification is always required for ticketed events.
              </p>
            )}

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Admin Notes (internal only)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Internal notes about this event..."
                disabled={modalMode === "view"}
                className="bg-slate-800 border-slate-700"
              />
            </div>
          </div>

          <DialogFooter>
            {modalMode === "view" ? (
              <Button variant="outline" onClick={() => setModalOpen(false)}>
                Close
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {modalMode === "create" ? "Create Event" : "Save Changes"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Event?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel &quot;{eventToCancel?.name}&quot;? This
              action cannot be undone. The event will be hidden from users.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Event</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelConfirm}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-700"
            >
              {cancelling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Cancel Event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EventCard({ event, onView, onEdit, onCancel }) {
  const status = event.derived_status || "scheduled";

  return (
    <Card className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-base font-medium text-slate-200">
              {event.name || "Untitled Event"}
            </CardTitle>
            <div className="flex flex-wrap gap-2 mt-2">
              {/* Type badge */}
              <Badge
                variant="outline"
                className={`${
                  event.type === "video"
                    ? "border-purple-500/50 text-purple-300"
                    : "border-blue-500/50 text-blue-300"
                }`}
              >
                {event.type === "video" ? (
                  <Video className="w-3 h-3 mr-1" />
                ) : (
                  <MessageSquare className="w-3 h-3 mr-1" />
                )}
                {event.type}
              </Badge>

              {/* Ticketed badge */}
              {event.ticket_required && (
                <Badge
                  variant="outline"
                  className="border-emerald-500/50 text-emerald-300"
                >
                  <DollarSign className="w-3 h-3 mr-1" />
                  {formatPrice(event.ticket_price_cents)}
                </Badge>
              )}

              {/* Verified badge */}
              {event.requires_verification && (
                <Badge
                  variant="outline"
                  className="border-blue-500/50 text-blue-300"
                >
                  <Shield className="w-3 h-3 mr-1" />
                  Verified
                </Badge>
              )}
            </div>
          </div>

          {/* Status badge */}
          <Badge className={`${STATUS_COLORS[status]} text-white`}>
            {STATUS_LABELS[status]}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="space-y-2 text-sm text-slate-400">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {formatDateTime(event.starts_at)} • {event.duration_minutes} min
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Capacity: {event.capacity}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onView}
            className="flex-1"
          >
            <Eye className="w-4 h-4 mr-1" />
            View
          </Button>
          {onEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              className="flex-1"
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
          {onCancel && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
